import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpRequest, HttpHandler, HttpInterceptor, HttpErrorResponse, HttpEvent, HttpHeaders } from '@angular/common/http';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, from, throwError } from 'rxjs';
import { ConfigService } from './config.service';
import { TranslateService } from '@ngx-translate/core';
import { SPINNER_SAFETY_MS, SpinnerService } from './SpinnerService';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
	private isRefreshing = false;
	private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

	constructor(
		private http: HttpClient,
		private configService: ConfigService,
		private route: Router,
		private translate: TranslateService,
		private spinnerService: SpinnerService,
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Spinner globale: opt-out via header X-No-Spinner (es. autocomplete, polling) o richiesta partita da una tabella
		// che mostra le sue righe skeleton (SpinnerService.silenzioso)
		const fineSilenziosa = this.spinnerService.inizioSilenzioso();
		const showGlobalSpinner = !req.headers.has('X-No-Spinner') && !fineSilenziosa;
		if (showGlobalSpinner) {
			// le chiamate lunghe per natura chiedono un safety timeout piu' lungo via HttpContext
			this.spinnerService.show('app', req.context.get(SPINNER_SAFETY_MS));
		}

		return from(this.buildHeaders(null)).pipe(
			switchMap((headers: HttpHeaders) => {
				const authReq = this.applyHeaders(req, headers);
				return next.handle(authReq).pipe(
					catchError((error: HttpErrorResponse) => {
						// Refresh reattivo: su 401 (escluso endpoint auth) proviamo a rinnovare e ritentare
						if (error.status === 401 && !this.isAuthEndpoint(req.url)) {
							return this.handle401(req, next);
						}
						return this.handleHttpError(error, req);
					}),
					finalize(() => {
						if (showGlobalSpinner) {
							this.spinnerService.hide('app');
						}
					}),
				);
			}),
			// anche su unsubscribe prima che la richiesta parta (dopo buildHeaders): la tabella non resta in skeleton
			finalize(() => fineSilenziosa?.()),
		);
	}

	/**
	 * Gestisce 401: se un refresh è già in corso, mette la chiamata in coda;
	 * altrimenti avvia il refresh e alla fine ritenta la richiesta originale.
	 */
	private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshTokenSubject.next(null);

			return from(this.refreshToken()).pipe(
				switchMap((newToken: string) => {
					this.isRefreshing = false;
					this.refreshTokenSubject.next(newToken);
					return from(this.buildHeaders(newToken)).pipe(switchMap((headers) => next.handle(this.applyHeaders(req, headers))));
				}),
				catchError((err) => {
					this.isRefreshing = false;
					this.refreshTokenSubject.next(null);
					this.forceLogout();
					return throwError(() => err);
				}),
			);
		}

		// Refresh già in corso: aspetta il nuovo token e ritenta
		return this.refreshTokenSubject.pipe(
			filter((token) => token !== null),
			take(1),
			switchMap(() => from(this.buildHeaders(null)).pipe(switchMap((headers) => next.handle(this.applyHeaders(req, headers))))),
		);
	}

	/**
	 * Chiama l'endpoint di login/refresh passando il currentUser (che contiene refresh_token).
	 * Aggiorna localStorage con i nuovi token.
	 */
	private refreshToken(): Promise<string> {
		const currentUser = this.getLocalStorageObjectJson('currentUser');
		const portale = this.getLocalStorageObjectJson('portale');
		if (!currentUser || !portale || !currentUser.refresh_token) {
			return Promise.reject('no_refresh_token');
		}
		const headers = new HttpHeaders().set('Authorization', `Bearer ${currentUser.token}`).set('X-Client', 'portal');
		return new Promise<string>((resolve, reject) => {
			this.http.post<any>(`${this.configService.portalUrl}login/${portale.pt_id}`, currentUser, { headers }).subscribe({
				next: (resp: any) => {
					if (resp && resp.token) {
						const merged = {
							...currentUser,
							token: resp.token,
							refresh_token: resp.refresh_token || currentUser.refresh_token,
						};
						localStorage.setItem('currentUser', JSON.stringify(merged));
						resolve(resp.token);
					} else {
						reject('refresh_invalid_response');
					}
				},
				error: (err: any) => reject(err),
			});
		});
	}

	private forceLogout() {
		this.configService.clearStorageKeepingCompany();
		this.route.navigate(['']);
		this.translate.get('message_error.jwt_expired').subscribe((message) => {
			this.configService.openErrorNotify(message);
		});
	}

	private isAuthEndpoint(url: string): boolean {
		return url.includes('/login') || url.includes('/refresh');
	}

	private buildHeaders(tokenOverride: string | null): Promise<HttpHeaders> {
		return new Promise((resolve) => {
			// X-Client: origine della chiamata, registrata nell'audit trail (ae_client).
			// Il portale e l'app mandano gli stessi header, senza questo sono indistinguibili.
			let headers = new HttpHeaders().set('Accept', 'application/json').set('X-Client', 'portal');
			const portale = this.getLocalStorageObjectJson('portale');
			if (portale) {
				headers = headers.set('portale', portale.pt_id.toString());
			}
			const currentUser = this.getLocalStorageObjectJson('currentUser');
			if (!currentUser) {
				resolve(headers);
				return;
			}
			const token = tokenOverride || currentUser.token;
			if (token) {
				headers = headers.set('Authorization', `Bearer ${token}`);
			}
			resolve(headers);
		});
	}

	private applyHeaders(req: HttpRequest<any>, headers: HttpHeaders): HttpRequest<any> {
		const headerObj: { [name: string]: string | string[] } = {};
		headers.keys().forEach((key: string) => {
			// Gli header impostati esplicitamente dal chiamante vincono su questi default:
			// serve p.es. ai download di file, dove 'Accept: application/json' farebbe
			// rispondere 500 (HttpMediaTypeNotAcceptableException) agli endpoint binari.
			if (req.headers.has(key)) return;
			headerObj[key] = headers.get(key) as string;
		});
		return req.clone({ setHeaders: headerObj });
	}

	private handleHttpError(error: HttpErrorResponse, req: HttpRequest<any>): Observable<never> {
		let msgError: any = 'Qualcosa è andato storto, riprova più tardi';

		if (error.status === 0 || error.status === 404) {
			msgError = 'Servizio non raggiungibile';
			this.configService.openErrorNotify(msgError);
			return throwError(() => msgError);
		}

		if (error.status === 500) {
			if (!req.headers.has('X-Silent')) {
				msgError = 'Errore servizio interno';
				this.configService.isUserSuperAdmin() ? this.configService.openErrorEnchantedNotify(error) : this.configService.openErrorNotify(msgError);
			}
			return throwError(() => msgError);
		}

		// error.error può essere stringa oppure { status, message, data } — estraggo in modo sicuro
		const raw = error.error;
		if (typeof raw === 'string') {
			msgError = raw;
		} else if (raw && typeof raw === 'object') {
			msgError = raw.message || raw.error || JSON.stringify(raw);
		}

		console.log('msgError', error.error);

		if (typeof msgError === 'string' && msgError.includes('KCS|')) {
			msgError = msgError.replace('KCS|', '');
		}

		if (typeof msgError === 'string' && msgError) {
			this.translate.get(msgError).subscribe((r) => {
				this.configService.openErrorNotify(r);
			});
		}

		if (error.status === 401) {
			// Qui ci arriviamo solo per endpoint auth (isAuthEndpoint) o se handle401 ha già fatto forceLogout.
			// Per sicurezza garantiamo il logout se siamo ancora autenticati.
			this.forceLogout();
		}

		// Il 403 NON è una sessione scaduta: nel portale è il diniego di un'operazione
		// (permesso mancante su una funzione, azione non consentita su quell'azienda) e
		// viene usato in decine di endpoint per messaggi di merito. Svuotare il
		// localStorage e riportare al login buttava fuori l'utente al primo diniego,
		// facendo perdere il lavoro in corso e nascondendo il motivo. Il logout resta
		// legato al solo 401, che è gestito sopra da forceLogout().
		if (error.status === 403 && !msgError) {
			msgError = 'Operazione non consentita';
			this.configService.openErrorNotify(msgError);
		}

		return throwError(() => msgError);
	}

	private getLocalStorageObjectJson(key: string) {
		const val = JSON.parse(localStorage.getItem(key) || '{}');
		return Object.keys(val).length === 0 ? null : val;
	}
}
